import type { Tone } from '../theme/tokens';

/** Option lists of the Customer Stories area, shared by filters and forms (values are the backend ids). */
export interface StoryOption {
    readonly title: string;
    readonly value: number | string;
}

export interface StoryStatusMeta {
    readonly id: number;
    readonly label: string;
    readonly tone: Tone;
}

/** `statusId` of a story. Id 7 is display-only (never selectable). */
export const STORY_STATUSES: readonly StoryStatusMeta[] = Object.freeze([
    { id: 1, label: 'Created', tone: 'info' },
    { id: 2, label: 'Pending review', tone: 'warning' },
    { id: 3, label: 'Approved', tone: 'success' },
    { id: 4, label: 'Rejected', tone: 'neutral' },
    { id: 5, label: 'Test data', tone: 'danger' },
    { id: 6, label: 'Error on review', tone: 'danger' },
    { id: 7, label: 'E!!!!!!!', tone: 'danger' },
] as StoryStatusMeta[]);

/** Statuses a moderator may pick in filters and the edit form. */
export const STORY_STATUS_OPTIONS: readonly StoryStatusMeta[] = STORY_STATUSES.filter((status) => status.id !== 7);

const STATUS_BY_ID: Readonly<Record<number, StoryStatusMeta>> = STORY_STATUSES.reduce((acc, s) => ({ ...acc, [s.id]: s }), {});

export const storyStatus = (id: unknown): StoryStatusMeta => {
    const numeric = Number(id);
    return (Number.isFinite(numeric) && STATUS_BY_ID[numeric]) || { id: numeric, label: 'Unknown', tone: 'neutral' };
};

/** Gift card `giftcardStatusId`. */
export const GIFT_CARD_STATUSES: Readonly<Record<number, { label: string; tone: Tone }>> = Object.freeze({
    1: { label: 'Created', tone: 'info' },
    2: { label: 'Sent', tone: 'success' },
    3: { label: 'Disabled', tone: 'neutral' },
    4: { label: 'Error', tone: 'danger' },
});

export const giftCardStatus = (id: unknown) => {
    const numeric = Number(id);
    return (Number.isFinite(numeric) && GIFT_CARD_STATUSES[numeric]) || { label: `Status ${id}`, tone: 'neutral' as Tone };
};

export const STORY_PUBLISHED_OPTIONS: readonly StoryOption[] = Object.freeze([
    { title: 'Published', value: '1' },
    { title: 'Not published', value: '0' },
]);

/** Square is three product ids; filters send `5,6,10`, forms save `6`. */
export const SQUARE_PRODUCT_FILTER_VALUE = '5,6,10';
export const SQUARE_PRODUCT_IDS = [5, 6, 10];

export const STORY_PRODUCT_OPTIONS: readonly StoryOption[] = Object.freeze([
    { title: 'Small', value: 1 },
    { title: 'Medium', value: 2 },
    { title: 'Large', value: 3 },
    { title: 'Mini', value: 7 },
    { title: 'Keychain', value: 4 },
    { title: 'Square', value: SQUARE_PRODUCT_FILTER_VALUE },
    { title: 'Extra Large', value: 11 },
]);

export const STORY_OCCASION_OPTIONS: readonly StoryOption[] = Object.freeze([
    { title: 'Birthday Gift', value: 1 },
    { title: 'Christmas Gift', value: 2 },
    { title: 'Father’s Day Gift', value: 3 },
    { title: 'Graduation Gift', value: 4 },
    { title: 'Mother’s Day Gift', value: 5 },
    { title: 'Wedding Gift', value: 6 },
    { title: 'Back To College Gift', value: 7 },
    { title: 'Passing of pet', value: 8 },
    { title: 'Other', value: 9 },
    { title: 'Valentines day Gift', value: 10 },
    { title: 'Anniversary Gift', value: 11 },
    { title: 'Graduation Gift', value: 12 },
    { title: 'Pride Month Gift', value: 13 },
    { title: 'Easter Gift', value: 14 },
    { title: 'Memorial Day Gift', value: 15 },
    { title: '4th Of July Gift', value: 16 },
    { title: 'Halloween Gift', value: 17 },
    { title: 'Thanksgiving Gift', value: 18 },
    { title: 'New Year’s Gift', value: 19 },
    { title: 'Baby Shower Gift', value: 20 },
    { title: 'Housewarming Gift', value: 21 },
    { title: 'Retirement Gift', value: 22 },
    { title: 'Engagement Gift', value: 23 },
    { title: 'Promotion Gift', value: 24 },
    { title: 'Back To School Gift', value: 25 },
    { title: 'Sports Achievements Gift', value: 26 },
    { title: 'Memorial Gift', value: 27 },
]);

export const STORY_RECIPIENT_OPTIONS: readonly StoryOption[] = Object.freeze([
    { title: 'Prefer not to answer', value: 1 },
    { title: 'Son', value: 2 },
    { title: 'Daughter', value: 3 },
    { title: 'Parent', value: 4 },
    { title: 'Friend', value: 5 },
    { title: 'Co-Worker', value: 6 },
    { title: 'Grandparent', value: 7 },
    { title: 'Myself', value: 8 },
    { title: 'Spouse', value: 9 },
    { title: 'Boyfriend/Girlfriend', value: 10 },
    { title: 'Child', value: 11 },
    { title: 'Other', value: 12 },
]);

/** Bunny Stream CDN that serves the story renditions. */
export const STORY_CDN = 'https://vz-e462438a-03a.b-cdn.net';
export const STORY_RENDITIONS = [1440, 1080, 720, 480, 360, 240];

/** `"<libraryId>/<guid>"` → guid. */
export const storyVideoId = (link: string | null | undefined): string => {
    if (!link) return '';
    const index = link.indexOf('/');
    return index === -1 ? link : link.substring(index + 1);
};

/** Bunny dashboard deep link for a `"<libraryId>/<guid>"` video link. */
export const bunnyDashboardUrl = (link: string | null | undefined): string | undefined => {
    if (!link || link.indexOf('/') === -1) return undefined;
    const libraryId = link.substring(0, link.indexOf('/'));
    const guid = storyVideoId(link);
    return `https://dash.bunny.net/stream/${libraryId}/library/videos?page=1&search=${guid}&videoId=${guid}`;
};
